import React, { useCallback, useEffect, useState } from 'react';

import { UserRole } from '@shared/types/user_role';

import { AccountInvoicesPanel } from '@admin/components/accounts/account_invoices_panel';
import { AccountPaymentsTable } from '@admin/components/accounts/payments_table';
import { AddCreditForm as BillingAddCreditForm } from '@admin/components/billing/charges/add_credit_form';
import { ChargeForm as BillingChargeForm } from '@admin/components/billing/charges/charge_form';
import { BillingSourceDialogButton } from '@admin/components/billing/sources/billing_source_dialog_button';
import { List as BillingSourcesList } from '@admin/components/billing/sources/list';
import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { ParcelCarrierAccountsPanel } from '@admin/components/logistics/parcel_carrier_accounts/panel';
import { StorageTreasuresAuctionList } from '@admin/components/storage_treasures/auction_list';
import { SubscriptionsPanel } from '@admin/components/subscriptions/panel';

/**
 * Billing tab on the account show page: subscriptions, payment methods, parcel carriers,
 * auctions, invoices, charges/credits, and payment history. Replaces Angular template
 * wiring (`inlineForms`, `collapsible-panel`) for this tab.
 */
export const AccountBillingTab: React.FC<{
  accountID: string;
  thirdPartyLogistics: boolean;
  billingDateChangeable: boolean;
  billingDateChangedOn: string | null;
  subscriptionBillingDay: number;
  suspendable: boolean;
  onSuspended(): void;
}> = ({
  accountID,
  thirdPartyLogistics,
  billingDateChangeable,
  billingDateChangedOn,
  subscriptionBillingDay,
  suspendable,
  onSuspended,
}) => {
  const [creditsAddOpen, setCreditsAddOpen] = useState(false);
  const [chargesNewOpen, setChargesNewOpen] = useState(false);
  const [chargeInitialKind, setChargeInitialKind] = useState<string | undefined>();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  useEffect(() => {
    const onBroadcast = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string; data?: { form?: string } }>).detail;
      if (!detail?.name) return;
      if (detail.name !== 'inlineform:hide' && detail.name !== 'inlineform:saved') return;
      const form = detail.data?.form;
      if (form === 'creditsAdd') setCreditsAddOpen(false);
      if (form === 'chargesNew') setChargesNewOpen(false);
    };
    document.addEventListener('broadcast', onBroadcast);
    return () => document.removeEventListener('broadcast', onBroadcast);
  }, []);

  const openChargesNew = useCallback(() => {
    setChargeInitialKind(undefined);
    setChargesNewOpen(true);
  }, []);

  const openChargesNewPrepayment = useCallback(() => {
    setChargeInitialKind('Prepayment');
    setChargesNewOpen(true);
  }, []);

  const closeChargesNew = useCallback(() => {
    setChargesNewOpen(false);
    setChargeInitialKind(undefined);
  }, []);

  return (
    <div id="billing-tab-content-inner">
      <div className="row">
        <div className="col-md-6">
          <SubscriptionsPanel accountID={accountID} />
        </div>

        <div className="col-md-6">
          <div className="panel" id="subscriptions-anchor">
            <div className="panel-heading">
              <h3 className="panel-title">Payment Methods On File</h3>
            </div>

            <div className="panel-body">
              <BillingSourcesList accountID={accountID} />
            </div>
            <div className="panel-footer">
              {creditsAddOpen && (
                <BillingAddCreditForm
                  accountID={accountID}
                  onCancel={() => setCreditsAddOpen(false)}
                  onSave={() => setCreditsAddOpen(false)}
                />
              )}
              {!creditsAddOpen && (
                <div className="text-right">
                  <BillingSourceDialogButton accountID={accountID} />{' '}
                  <Roles show={[UserRole.BillingAdmin, UserRole.Care, UserRole.SelfStorageAssociate]}>
                    <button type="button" className="btn btn-primary" onClick={() => setCreditsAddOpen(true)}>
                      Customer Resolution
                    </button>
                  </Roles>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {thirdPartyLogistics && (
        <div className="row">
          <div className="col-md-6">
            <ParcelCarrierAccountsPanel accountID={accountID} />
          </div>
        </div>
      )}

      <StorageTreasuresAuctionList auctionedAccountID={accountID} hideFilters title="Auctions Held" />

      <AccountInvoicesPanel
        accountID={accountID}
        billingDateChangeable={billingDateChangeable}
        billingDateChangedOn={billingDateChangedOn}
        subscriptionBillingDay={subscriptionBillingDay}
        suspendable={suspendable}
        onSuspended={onSuspended}
        onShowChargesNew={openChargesNew}
        onShowChargesNewPrepayment={openChargesNewPrepayment}
        footerActionsVisible={!chargesNewOpen}
      />

      {chargesNewOpen && (
        <BillingChargeForm
          key={`charge-${chargeInitialKind ?? 'default'}`}
          accountID={accountID}
          initialKind={chargeInitialKind}
          onCancel={closeChargesNew}
          onSave={closeChargesNew}
        />
      )}

      <Panel>
        <Panel.Header>
          <Panel.Collapse collapsed={collapsed} onChange={setCollapsed} />
          <Panel.Title>Credit Card Charges &amp; Refunds</Panel.Title>
        </Panel.Header>
        {!collapsed && (
          <Panel.Body>
            <p>
              This shows the actual charges and refunds in the order they would appear on a credit card statement,
              instead of just the invoice amount and bill date. Click on a transaction to learn more about it.
            </p>
            <AccountPaymentsTable accountID={accountID} />
          </Panel.Body>
        )}
      </Panel>
    </div>
  );
};
